import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../utils/config';
import { useItemsHistory } from './useActionHistory';
import { useTryOnMode } from './useTryOnMode';
import { useVirtualTryOn } from './useVirtualTryOn';
import { setStorageItem, getStorageItem, removeStorageItem } from '../utils/storageUtils';

const useMyClosetState = (guest) => {
  const navigate = useNavigate();

  // Core state
  const [clothingItems, setClothingItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [placeholderWidth, setPlaceholderWidth] = useState(0);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Interaction state
  const [isMoving, setIsMoving] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0 });
  const [resizeStartDimensions, setResizeStartDimensions] = useState({ width: 0, height: 0 });
  const [rotateStartAngle, setRotateStartAngle] = useState(0);

  // UI state
  const [isLookPopupOpen, setIsLookPopupOpen] = useState(false);
  const [lookName, setLookName] = useState('');
  const [isNoItemsPopupOpen, setIsNoItemsPopupOpen] = useState(false);
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // Background state is now managed by Redux - removed from here

  // Positioning helpers state
  const [showGrid, setShowGrid] = useState(false);
  const [showSnapGuides, setShowSnapGuides] = useState(true);
  const [gridSize, setGridSize] = useState(20);
  const [snapLines, setSnapLines] = useState([]);
  const [showPositioningControls, setShowPositioningControls] = useState(false);
  const [multiSelectedItems, setMultiSelectedItems] = useState([]);


  // Refs
  const folderIconRef = useRef(null);
  const placeholderRef = useRef(null);

  // History management
  const {
    items: droppedItems,
    updateItems: setDroppedItems,
    updateItemsRealtime: setDroppedItemsRealtime,
    commitItems: commitDroppedItems,
    undo: undoAction,
    redo: redoAction,
    getHistoryInfo,
    canUndo,
    canRedo
  } = useItemsHistory([]);

  // Virtual Try-On hooks
  const {
    isVirtualMode,
    isAvailable,
    personImage,
    toggleMode,
    updatePersonImage,
    getStatusMessage,
    setIsVirtualMode,
    setIsAvailable
  } = useTryOnMode();

  const {
    isLoading: isTryOnLoading,
    error: tryOnError,
    result: tryOnResult,
    tryOnGarment,
    clearError: clearTryOnError,
    clearResult: clearTryOnResult
  } = useVirtualTryOn();

  const [virtualTryOnResults, setVirtualTryOnResults] = useState([]);

  // Track canvas size changes with ResizeObserver
  useEffect(() => {
    if (!placeholderRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setPlaceholderWidth(width);
        setPlaceholderHeight(height);
      }
    });

    resizeObserver.observe(placeholderRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Initialize component
  useEffect(() => {
    if (placeholderRef.current) {
      setPlaceholderWidth(placeholderRef.current.offsetWidth);
      setPlaceholderHeight(placeholderRef.current.offsetHeight);

      // Load current look after setting dimensions
      try {
        const currentLook = getStorageItem('currentLook');
        if (currentLook && Array.isArray(currentLook)) {
          setDroppedItems(currentLook, 'load-look');
        }
      } catch (error) {
        console.error('Error loading current look from storage:', error);
        removeStorageItem('currentLook');
      }

      // Background settings are now managed by Redux - removed from here
    }

    if (guest) {
      // Get clothes from storage for guest mode
      try {
        const guestClothes = getStorageItem('guestClothes') || [];
        console.log('[Closet] Loading guest clothes from localStorage:', guestClothes.length, 'items');
        if (Array.isArray(guestClothes)) {
          setClothingItems(guestClothes);
          console.log('[Closet] Guest clothes loaded into state');
        }
      } catch (error) {
        console.error('Error loading guest clothes from storage:', error);
        removeStorageItem('guestClothes');
        setClothingItems([]);
      }
    } else {
      // Original database fetching code
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      console.log('[Closet] Authentication Debug:', {
        hasUser: !!user,
        hasToken: !!token,
        userValue: user,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      // Check if user is authenticated
      if (!user || !token) {
        console.warn('[Closet] User not authenticated, redirecting to login');
        localStorage.clear();
        navigate('/');
        return;
      }

      axios(`${BACKEND_URL}/closet-clothes?users_permissions_users=${user}&_sort=updated_at:desc`, {
        method: 'get',
        headers: {
          Authorization: 'Bearer ' + token
        }
      })
        .then(res => {
          if (res.status !== 200) {
            console.warn('[Closet] API returned non-200 status:', res.status);
            localStorage.clear();
            navigate('/');
            return;
          }
          setClothingItems(res.data);
        })
        .catch(error => {
          console.error('Error fetching clothing items:', error);

          // Only clear and redirect if it's an auth error
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.warn('[Closet] Authentication failed, clearing session');
            localStorage.clear();
            navigate('/');
          } else {
            // For other errors, just log and continue with empty items
            console.warn('[Closet] API error, continuing with empty items');
            setClothingItems([]);
          }
        });
    }
  }, [navigate, guest, setDroppedItems]);

  // Save dropped items using intelligent storage management
  useEffect(() => {
    if (droppedItems.length > 0) {
      const look = droppedItems.map(item => ({
        id: item.id,
        image: item.image,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        rotation: item.rotation || 0,
        zIndex: item.zIndex || 1,
        isVirtualTryOn: item.isVirtualTryOn || false,
        originalImage: item.originalImage,
        tryOnMetadata: item.tryOnMetadata
      }));

      // Use intelligent storage with high priority for current look
      setStorageItem('currentLook', look, {
        priority: 'high',
        maxAge: 24 * 60 * 60 * 1000 // 24 hours
      });
    } else {
      removeStorageItem('currentLook');
    }
  }, [droppedItems]);

  // Background persistence is now handled by Redux middleware - removed from here

  return {
    // Core state
    clothingItems,
    setClothingItems,
    droppedItems,
    setDroppedItems,
    setDroppedItemsRealtime,
    commitDroppedItems,
    selectedItem,
    setSelectedItem,
    placeholderWidth,
    setPlaceholderWidth,
    placeholderHeight,
    setPlaceholderHeight,
    dragOffset,
    setDragOffset,

    // Interaction state
    isMoving,
    setIsMoving,
    isResizing,
    setIsResizing,
    isRotating,
    setIsRotating,
    resizeStartPos,
    setResizeStartPos,
    resizeStartDimensions,
    setResizeStartDimensions,
    rotateStartAngle,
    setRotateStartAngle,

    // UI state
    isLookPopupOpen,
    setIsLookPopupOpen,
    lookName,
    setLookName,
    isNoItemsPopupOpen,
    setIsNoItemsPopupOpen,
    isSuccessPopupOpen,
    setIsSuccessPopupOpen,
    isEditMode,
    setIsEditMode,
    isBulkSelectMode,
    setIsBulkSelectMode,
    selectedItems,
    setSelectedItems,

    // Background state is now managed by Redux - removed from here

    // Positioning helpers state
    showGrid,
    setShowGrid,
    showSnapGuides,
    setShowSnapGuides,
    gridSize,
    setGridSize,
    snapLines,
    setSnapLines,
    showPositioningControls,
    setShowPositioningControls,
    multiSelectedItems,
    setMultiSelectedItems,


    // Refs
    folderIconRef,
    placeholderRef,

    // History
    undoAction,
    redoAction,
    getHistoryInfo,
    canUndo,
    canRedo,

    // Virtual Try-On
    isVirtualMode,
    isAvailable,
    personImage,
    toggleMode,
    updatePersonImage,
    getStatusMessage,
    setIsVirtualMode,
    setIsAvailable,
    isTryOnLoading,
    tryOnError,
    tryOnResult,
    tryOnGarment,
    clearTryOnError,
    clearTryOnResult,
    virtualTryOnResults,
    setVirtualTryOnResults,

    // Navigation
    navigate
  };
};

export default useMyClosetState;